import { publicHolidays } from "../data/holidays";
import { PlanKey } from "../components/task/payment/planTypes";

export const DONE_MODAL_MESSAGES = {
  // 오늘 휴일, 내일도 휴일
  HOLIDAY_TODAY_AND_TOMORROW: {
    title: "휴일 이후 다음 활동이 준비돼요",
    subtitle: "그동안 함께 연습한 것들을 일상에 스스로 적용해보세요.",
  },
  // 오늘 휴일, 내일 평일
  HOLIDAY_TODAY_WORKDAY_TOMORROW: {
    title: "내일 다음 활동이 준비돼요",
    subtitle: "그동안 함께 연습한 것들을 일상에 스스로 적용해보세요.",
  },
  // 오늘 평일, 내일 휴일, 이미 당일에 업데이트 받은 경우 (00시 이후)
  WORKDAY_TODAY_HOLIDAY_TOMORROW_UPDATED_TODAY: {
    title: "휴일 이후 다음 활동이 준비돼요",
    subtitle: "활동은 하루에 최대 한 번 업데이트 돼요.",
  },
  // 오늘 평일, 내일 휴일, 15시 이후 마친 경우
  WORKDAY_TODAY_HOLIDAY_TOMORROW_FINISH_AFTER_3PM: {
    title: "휴일 이후 다음 활동이 준비돼요",
    subtitle:
      "평일 오후 3시 이후에 활동을 마치면 다음 업무일에 새 활동이 준비돼요.",
  },
  // 오늘 내로 준비 (코치 확인 후 연락)
  PREPARE_TODAY: {
    title: "오늘 내로 다음 활동이 준비돼요",
    subtitle: "코치 선생님이 확인 후 연락을 드릴 거예요. 조금만 기다려주세요.",
  },
  // 내일 준비 (15시 이후 마친 경우 등)
  PREPARE_TOMORROW_FINISH_AFTER_3PM: {
    title: "내일 다음 활동이 준비돼요",
    subtitle:
      "평일 오후 3시 이후에 활동을 마치면 다음 업무일에 새 활동이 준비돼요.",
  },
  // 내일 준비 (이미 당일 업데이트 받은 경우)
  PREPARE_TOMORROW_UPDATED_TODAY: {
    title: "내일 다음 활동이 준비돼요",
    subtitle: "활동은 하루에 최대 한 번 업데이트 돼요.",
  },
  // [master] 오늘 평일, 내일 휴일, 당일 업데이트를 2회 이상 받은 경우
  WORKDAY_TODAY_HOLIDAY_TOMORROW_UPDATED_TODAY_MAX2: {
    title: "휴일 이후 다음 활동이 준비돼요",
    subtitle: "활동은 하루에 최대 두 번 업데이트 돼요.",
  },
  // [master] 내일 준비 (당일 업데이트를 2회 이상 받은 경우)
  PREPARE_TOMORROW_UPDATED_TODAY_MAX2: {
    title: "내일 다음 활동이 준비돼요",
    subtitle: "활동은 하루에 최대 두 번 업데이트 돼요.",
  },
  // [starter] 오늘 평일, 내일 휴일, 당일 업데이트를 1회 이상 받은 경우
  WORKDAY_TODAY_HOLIDAY_TOMORROW_UPDATED_TODAY_MAX1: {
    title: "휴일 이후 다음 활동이 준비돼요",
    subtitle: "활동은 하루에 최대 한 번 업데이트 돼요.",
  },
  // [starter] 내일 준비 (당일 업데이트를 1회 이상 받은 경우)
  PREPARE_TOMORROW_UPDATED_TODAY_MAX1: {
    title: "내일 다음 활동이 준비돼요",
    subtitle: "활동은 하루에 최대 한 번 업데이트 돼요.",
  },
  // [starter] 이번 주 한도 도달 (주 2회)
  PREPARE_NEXT_WEEK_WEEKLY_LIMIT_REACHED: {
    title: "다음 주에 다음 활동이 준비돼요",
    subtitle: "활동은 일주일에 최대 두 번 업데이트 돼요.",
  },
} as const;

interface ModalContentParams {
  now: Date;
  plan?: PlanKey;
  todayTaskUpdateCount?: number;
  weeklyTaskUpdateCount?: number;
}

/**
 * 완료 버튼 클릭 시 표시될 모달의 제목과 부제를 결정하는 함수
 * @param params - lastTaskUpdatedAt: 마지막 과제 업데이트 시간, now: 현재 시간 (클릭 시점)
 * @returns { title: string, subtitle: string, key: string }
 */
export const getDoneModalContent = ({
  now,
  plan = "challenger",
  todayTaskUpdateCount,
  weeklyTaskUpdateCount,
}: ModalContentParams): { title: string; subtitle: string; key: string } => {
  const isHoliday = (date: Date): boolean => {
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return true;
    }
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;
    return publicHolidays.includes(formattedDate);
  };

  try {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const isTodayHoliday_ = isHoliday(today);
    const isTomorrowHoliday_ = isHoliday(tomorrow);

    // 분기 1: 오늘이 휴일인 경우
    if (isTodayHoliday_) {
      if (isTomorrowHoliday_) {
        return { ...DONE_MODAL_MESSAGES.HOLIDAY_TODAY_AND_TOMORROW, key: "HOLIDAY_TODAY_AND_TOMORROW" };
      } else {
        return { ...DONE_MODAL_MESSAGES.HOLIDAY_TODAY_WORKDAY_TOMORROW, key: "HOLIDAY_TODAY_WORKDAY_TOMORROW" };
      }
    }
    // 분기 2: 오늘이 평일인 경우
    else {
      const todayStart = new Date(today);
      todayStart.setHours(0, 0, 0, 0);
      const today3PM = new Date(today);
      today3PM.setHours(15, 0, 0, 0);

      // 15시 이후 마침
      const isFinishingAfter3PM = now > today3PM;

      // challenger 플랜: todayTaskUpdateCount 기준으로 분기 (1회 이상 vs 0회). count 미지정 시 lastTaskUpdatedAt 기반으로 보조 판단.
      if (plan === "challenger") {
        const todayUpdatesForChallenger =
          typeof todayTaskUpdateCount === "number" ? todayTaskUpdateCount : 0;
        const updatedToday = todayUpdatesForChallenger >= 1;

        if (isTomorrowHoliday_) {
          if (updatedToday) {
            return { ...DONE_MODAL_MESSAGES.WORKDAY_TODAY_HOLIDAY_TOMORROW_UPDATED_TODAY, key: "WORKDAY_TODAY_HOLIDAY_TOMORROW_UPDATED_TODAY" };
          } else if (isFinishingAfter3PM) {
            return { ...DONE_MODAL_MESSAGES.WORKDAY_TODAY_HOLIDAY_TOMORROW_FINISH_AFTER_3PM, key: "WORKDAY_TODAY_HOLIDAY_TOMORROW_FINISH_AFTER_3PM" };
          } else {
            return { ...DONE_MODAL_MESSAGES.PREPARE_TODAY, key: "PREPARE_TODAY" };
          }
        } else {
          if (updatedToday) {
            return { ...DONE_MODAL_MESSAGES.PREPARE_TOMORROW_UPDATED_TODAY, key: "PREPARE_TOMORROW_UPDATED_TODAY" };
          } else if (isFinishingAfter3PM) {
            return { ...DONE_MODAL_MESSAGES.PREPARE_TOMORROW_FINISH_AFTER_3PM, key: "PREPARE_TOMORROW_FINISH_AFTER_3PM" };
          } else {
            return { ...DONE_MODAL_MESSAGES.PREPARE_TODAY, key: "PREPARE_TODAY" };
          }
        }
      }

      // starter / master 분기 처리
      if (plan === "starter") {
        const todayUpdates =
          typeof todayTaskUpdateCount === "number" ? todayTaskUpdateCount : 0;
        const weeklyUpdates =
          typeof weeklyTaskUpdateCount === "number" ? weeklyTaskUpdateCount : 0;

        // 주간 한도(주 2회) 도달 시: 다음 주 준비
        if (weeklyUpdates >= 2) {
          return { ...DONE_MODAL_MESSAGES.PREPARE_NEXT_WEEK_WEEKLY_LIMIT_REACHED, key: "PREPARE_NEXT_WEEK_WEEKLY_LIMIT_REACHED" };
        }

        // 오늘 이미 업데이트 받았음 (일 최대 1회)
        if (todayUpdates >= 1) {
          if (isTomorrowHoliday_) {
            return { ...DONE_MODAL_MESSAGES.WORKDAY_TODAY_HOLIDAY_TOMORROW_UPDATED_TODAY_MAX1, key: "WORKDAY_TODAY_HOLIDAY_TOMORROW_UPDATED_TODAY_MAX1" };
          } else {
            return { ...DONE_MODAL_MESSAGES.PREPARE_TOMORROW_UPDATED_TODAY_MAX1, key: "PREPARE_TOMORROW_UPDATED_TODAY_MAX1" };
          }
        }

        // 오늘 0회이고 이번 주 2회 미만: 15시 규칙 적용
        if (isTomorrowHoliday_) {
          if (isFinishingAfter3PM) {
            return { ...DONE_MODAL_MESSAGES.WORKDAY_TODAY_HOLIDAY_TOMORROW_FINISH_AFTER_3PM, key: "WORKDAY_TODAY_HOLIDAY_TOMORROW_FINISH_AFTER_3PM" };
          } else {
            return { ...DONE_MODAL_MESSAGES.PREPARE_TODAY, key: "PREPARE_TODAY" };
          }
        } else {
          if (isFinishingAfter3PM) {
            return { ...DONE_MODAL_MESSAGES.PREPARE_TOMORROW_FINISH_AFTER_3PM, key: "PREPARE_TOMORROW_FINISH_AFTER_3PM" };
          } else {
            return { ...DONE_MODAL_MESSAGES.PREPARE_TODAY, key: "PREPARE_TODAY" };
          }
        }
      } else {
        // master 플랜: 당일 업데이트 2회 이상 시 한도 도달 처리
        const todayUpdates =
          typeof todayTaskUpdateCount === "number" ? todayTaskUpdateCount : 0;
        const hasReachedDailyLimit = todayUpdates >= 2; // 최대 두 번

        if (isTomorrowHoliday_) {
          if (hasReachedDailyLimit) {
            return { ...DONE_MODAL_MESSAGES.WORKDAY_TODAY_HOLIDAY_TOMORROW_UPDATED_TODAY_MAX2, key: "WORKDAY_TODAY_HOLIDAY_TOMORROW_UPDATED_TODAY_MAX2" };
          } else if (isFinishingAfter3PM) {
            return { ...DONE_MODAL_MESSAGES.WORKDAY_TODAY_HOLIDAY_TOMORROW_FINISH_AFTER_3PM, key: "WORKDAY_TODAY_HOLIDAY_TOMORROW_FINISH_AFTER_3PM" };
          } else {
            return { ...DONE_MODAL_MESSAGES.PREPARE_TODAY, key: "PREPARE_TODAY" };
          }
        } else {
          if (hasReachedDailyLimit) {
            return { ...DONE_MODAL_MESSAGES.PREPARE_TOMORROW_UPDATED_TODAY_MAX2, key: "PREPARE_TOMORROW_UPDATED_TODAY_MAX2" };
          } else if (isFinishingAfter3PM) {
            return { ...DONE_MODAL_MESSAGES.PREPARE_TOMORROW_FINISH_AFTER_3PM, key: "PREPARE_TOMORROW_FINISH_AFTER_3PM" };
          } else {
            return { ...DONE_MODAL_MESSAGES.PREPARE_TODAY, key: "PREPARE_TODAY" };
          }
        }
      }
    }
  } catch (e) {
    return {
      title: "주어진 활동을 모두 마쳤어요",
      subtitle: "평일 업무시간에 코치 선생님이 확인 후에 메시지를 드릴 거예요.",
      key: "DEFAULT_ERROR",
    };
  }
};
